import { useEffect, useMemo, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Check, Eye, EyeOff, LogOut, MessagesSquare, Moon, Sun, Vote } from 'lucide-react';
import {
  NO_KILL,
  ROLES,
  ROLE_INFO,
  SKIP_VOTE,
  isMafiaTeam,
  type LobbyView,
  type NightActionKind,
  type PlayerView,
  type PublicPlayer,
} from '@mafia/core';
import { emit } from '@/lib/socket';
import { PHASE_NAMES, plural, seatLabel } from '@/lib/format';
import { Button, Card, Modal } from '@/components/ui';
import { PlayerCard, type CardInfo, type Mark } from './PlayerCard';
import { RoleReveal } from './RoleReveal';
import { GameOver } from './GameOver';
import { Stopwatch, Timer } from './Timer';
import { ModeratorScreen } from './ModeratorScreen';
import { NightReveal } from './NightReveal';

type Mode = 'night' | 'dummy' | 'nominate' | 'vote' | 'none';

const ACTION_LABELS: Record<NightActionKind, { tab: string; hint: string; confirm?: string }> = {
  MAFIA_KILL: {
    tab: '🔫 Выстрел',
    hint: 'Выберите жертву или «Не убивать». Сообщники видят ваш выбор, его можно менять до конца ночи.',
  },
  DON_CHECK: { tab: '🎩 Поиск шерифа', hint: 'Проверьте одного игрока: шериф он или нет. Одна проверка за ночь.', confirm: 'Проверить' },
  SHERIFF_CHECK: { tab: '⭐ Проверка', hint: 'Проверьте одного игрока: мафия он или нет. Одна проверка за ночь.', confirm: 'Проверить' },
  HEAL: { tab: '💉 Лечение', hint: 'Кого вылечить этой ночью? Выбор можно менять до конца ночи.' },
  LOVER_VISIT: {
    tab: '🦋 Ночь с игроком',
    hint: 'С кем провести ночь? Этот игрок днём будет молчать и не голосовать. Если мафия придёт к нему — он выживет; если к вам — погибнете оба (если доктор не вылечит вас).',
  },
  MANIAC_KILL: { tab: '🔪 Жертва', hint: 'Кого убить этой ночью? Выбор можно менять до конца ночи.' },
};

export function GameScreen({ view, lobby }: { view: PlayerView; lobby: LobbyView | null }) {
  if (view.phase === 'GAME_OVER') return <GameOver view={view} />;
  if (view.isModerator) return <ModeratorScreen view={view} lobby={lobby} />;
  return <ActiveGame view={view} lobby={lobby} />;
}

function ActiveGame({ view, lobby }: { view: PlayerView; lobby: LobbyView | null }) {
  const me = view.me;
  const [tab, setTab] = useState<NightActionKind | null>(null);
  const [pending, setPending] = useState<number | null>(null);
  const [suspect, setSuspect] = useState<number | null>(null);
  const [acked, setAcked] = useState(false);
  const [showRole, setShowRole] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [busy, setBusy] = useState(false);

  // Смена фазы или спикера сбрасывает незавершённый выбор.
  useEffect(() => {
    setPending(null);
    setSuspect(null);
  }, [view.phase, view.day, view.speakerSeat]);

  // Каждую ночь начинаем с главного действия (у дона — выстрел, а не проверка).
  useEffect(() => setTab(null), [view.day]);

  const isNight = view.phase === 'NIGHT';
  // Ночью активным ролям всё нужно для хода; днём — только если игрок сам открыл роль.
  const secretsVisible = showRole || isNight || view.phase === 'ROLE_REVEAL';
  const available = me?.nightActions ?? [];
  const sel = me?.nightSelection ?? {};
  // Несколько действий (дон: выстрел, затем проверка) идут шагами, а не вкладками.
  const steps = me && isNight && me.alive ? ROLES[me.role].nightActions : [];
  const stepDone = (a: NightActionKind) => (a === 'DON_CHECK' || a === 'SHERIFF_CHECK' ? !available.includes(a) : sel[a] !== undefined);
  const nextStep = available.find((a) => !stepDone(a)) ?? available[0] ?? null;
  const action = tab && available.includes(tab) ? tab : nextStep;

  const mode: Mode = (() => {
    if (!me?.alive) return isNight ? 'dummy' : 'none';
    if (isNight) return action ? 'night' : 'dummy';
    if (view.phase === 'DAY_SPEECH' && me.canNominate) return 'nominate';
    // Голос можно менять, пока голосование открыто.
    if (view.phase === 'VOTING' && !me.silenced) return 'vote';
    return 'none';
  })();

  const avatars = useMemo(() => new Map(lobby?.members.map((m) => [m.userId, m.avatarId]) ?? []), [lobby]);

  function canTarget(p: PublicPlayer): boolean {
    if (!p.alive || !me) return false;
    switch (mode) {
      case 'night':
        if (action === 'MAFIA_KILL') return !(p.role && isMafiaTeam(p.role));
        if (action === 'HEAL') return !me.healForbidden.includes(p.seat);
        return p.seat !== me.seat;
      case 'dummy':
        return p.seat !== me.seat;
      case 'nominate':
        return p.seat !== me.seat && (!view.nominations.includes(p.seat) || p.seat === me.myNomination);
      case 'vote':
        return view.voting?.candidates.includes(p.seat) ?? false;
      default:
        return false;
    }
  }

  async function run(event: Parameters<typeof emit>[0], payload?: unknown) {
    setBusy(true);
    const res = await emit(event, payload);
    setBusy(false);
    return res.ok;
  }

  // Одно нажатие — без подтверждений: выбор ночью, выставление и голос можно поменять.
  // Подтверждение остаётся только у проверок — их нельзя отменить.
  function choose(target: number) {
    if (mode === 'dummy') return setSuspect(target);
    if (mode === 'night' && action) {
      if (ACTION_LABELS[action].confirm) return setPending(target);
      return void run('night:action', { action, target });
    }
    if (mode === 'nominate') return void run('day:nominate', { target });
    if (mode === 'vote') return void run('vote:cast', { target });
  }

  async function confirmCheck() {
    if (pending === null || !action) return;
    if (await run('night:action', { action, target: pending })) setPending(null);
  }

  function infoFor(p: PublicPlayer): CardInfo {
    const marks: Mark[] = [];
    if (isNight) {
      if (sel.MAFIA_KILL === p.seat || sel.MANIAC_KILL === p.seat) marks.push('kill');
      if (sel.HEAL === p.seat) marks.push('heal');
      if (sel.SHERIFF_CHECK === p.seat || sel.DON_CHECK === p.seat) marks.push('check');
      if (suspect === p.seat) marks.push('suspect');
    }
    if (me?.myVote === p.seat) marks.push('vote');
    if (isNight && sel.LOVER_VISIT === p.seat) marks.push('visit');
    if (pending === p.seat) marks.push('pending');

    const votes = view.voting?.votes ?? null;
    const voters = votes ? Object.entries(votes).filter(([, t]) => t === p.seat).map(([v]) => Number(v)) : [];
    const lastCheck = [...(me?.checks ?? [])].reverse().find((c) => c.target === p.seat);

    return {
      isMe: p.seat === me?.seat,
      // Днём, пока роль скрыта, не показываем ничего, что выдаёт её соседям по столу.
      hideRole: !secretsVisible && !!me && (p.seat === me.seat || (isMafiaTeam(me.role) && !!p.role && isMafiaTeam(p.role))),
      avatarId: avatars.get(p.userId) ?? null,
      speaking: view.speakerSeat === p.seat,
      silenced: view.silencedSeat === p.seat,
      nominated: view.nominations.includes(p.seat),
      candidate: view.voting?.candidates.includes(p.seat) ?? false,
      votes: votes ? voters.length : null,
      voters,
      mafiaVoters: view.mafiaVotes
        ? Object.entries(view.mafiaVotes).filter(([, t]) => t === p.seat).map(([s]) => Number(s))
        : [],
      check: secretsVisible && lastCheck ? { positive: lastCheck.positive, kind: me!.role === 'SHERIFF' ? 'SHERIFF' : 'DON' } : null,
      marks,
    };
  }

  const iSpeak = me && view.speakerSeat === me.seat;
  const choices = choiceOptions(view, mode);

  return (
    <div className={clsx('min-h-[calc(100dvh-3.5rem)] pb-28 transition-colors duration-700', isNight && 'phase-night')}>
      <div className="mx-auto max-w-5xl px-3 py-4 sm:px-4">
        {/* Шапка фазы */}
        <div className="mb-3 flex items-center gap-3">
          <div className={clsx('flex h-11 w-11 items-center justify-center rounded-xl', isNight ? 'bg-indigo-500/20 text-indigo-300' : 'bg-gold-400/15 text-gold-300')}>
            {isNight ? <Moon className="h-5 w-5" /> : view.phase === 'VOTING' ? <Vote className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-widest text-zinc-500">
              {view.day > 0 ? `${isNight ? 'Ночь' : 'День'} ${view.day}` : 'Начало игры'}
            </p>
            <h1 className="truncate font-display text-xl sm:text-2xl">{PHASE_NAMES[view.phase]}</h1>
          </div>
          {view.pace === 'manual' ? (
            <Stopwatch since={view.phaseStartedAt} className="text-2xl text-zinc-400 sm:text-3xl" />
          ) : (
            <Timer endsAt={view.phaseEndsAt} className="text-3xl sm:text-4xl" />
          )}
        </div>

        {me && view.phase !== 'ROLE_REVEAL' && (
          <button
            onClick={() => setShowRole(!showRole)}
            className="mb-3 flex w-full items-center gap-2 rounded-xl border border-white/5 bg-ink-900/70 px-3 py-2 text-left text-sm"
          >
            {showRole ? <EyeOff className="h-4 w-4 text-zinc-500" /> : <Eye className="h-4 w-4 text-zinc-500" />}
            <span className="text-zinc-500">Моя роль:</span>
            {showRole ? (
              <span className={isMafiaTeam(me.role) ? 'text-blood-400' : 'text-zinc-100'}>
                {ROLE_INFO[me.role].emoji} {ROLE_INFO[me.role].name} · место №{me.seat}
              </span>
            ) : (
              <span className="text-zinc-600">скрыта — нажмите, чтобы показать</span>
            )}
            {!me.alive && <span className="ml-auto text-xs text-zinc-500">вы выбыли</span>}
          </button>
        )}

        <Banner>{bannerText(view, mode, action)}</Banner>

        {mode === 'night' && steps.length > 1 && (
          <ol className="mb-3 flex gap-2">
            {steps.map((a, i) => {
              const done = stepDone(a);
              const open = available.includes(a);
              return (
                <li key={a} className="flex-1">
                  <button
                    disabled={!open}
                    aria-current={a === action ? 'step' : undefined}
                    onClick={() => {
                      setTab(a);
                      setPending(null);
                    }}
                    className={clsx(
                      'flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm transition',
                      a === action ? 'bg-white/10 text-white ring-1 ring-gold-400/50' : 'bg-ink-900 text-zinc-500 hover:text-zinc-300',
                      !open && 'opacity-60',
                    )}
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs">
                      {done ? <Check className="h-3.5 w-3.5 text-emerald-300" /> : i + 1}
                    </span>
                    {ACTION_LABELS[a].tab}
                  </button>
                </li>
              );
            })}
          </ol>
        )}

        {choices && <ChoicePanel {...choices} busy={busy} onChoose={choose} />}

        {view.phase === 'ROLE_REVEAL' && <RoleReveal view={view} />}

        <div className="grid grid-cols-3 gap-2.5 pt-2 sm:grid-cols-4 lg:grid-cols-5">
          {view.players.map((p) => (
            <PlayerCard key={p.seat} p={p} info={infoFor(p)} selectable={canTarget(p)} onClick={() => choose(p.seat)} />
          ))}
        </div>

        <SidePanels view={view} secretsVisible={secretsVisible} />
      </div>

      {/* Утро: анимация убийства и любовницы поверх стола; длительность — таймер утра или ведущий */}
      <NightReveal view={view} avatars={avatars} />

      {/* Панель действий */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-white/5 bg-ink-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center gap-2 px-3 py-3 pb-[max(.75rem,env(safe-area-inset-bottom))] sm:px-4">
          <div className="flex flex-1 flex-wrap gap-2">
            {view.phase === 'ROLE_REVEAL' && me && (
              <Button
                size="lg"
                variant="gold"
                className="flex-1 sm:flex-none"
                disabled={acked}
                loading={busy}
                onClick={async () => setAcked(await run('role:ack'))}
              >
                {acked ? 'Ждём остальных…' : 'Я запомнил роль'}
              </Button>
            )}
            {iSpeak && me.canPassSpeech && (
              <Button size="lg" variant="gold" className="flex-1 sm:flex-none" loading={busy} onClick={() => run('speech:pass')}>
                {view.phase === 'LAST_WORD' ? 'Завершить последнее слово' : 'Передать слово'}
              </Button>
            )}
            {iSpeak && !me.canPassSpeech && <p className="self-center text-sm text-zinc-400">Ваша речь — слово передаст ведущий.</p>}
            {mode === 'night' && action === 'MAFIA_KILL' && (
              <Button
                size="lg"
                variant={sel.MAFIA_KILL === NO_KILL ? 'primary' : 'secondary'}
                className="flex-1 sm:flex-none"
                loading={busy}
                onClick={() => run('night:action', { action: 'MAFIA_KILL', target: NO_KILL })}
              >
                {sel.MAFIA_KILL === NO_KILL ? '✓ Не убивать' : 'Не убивать'}
              </Button>
            )}
            {pending !== null && action && (
              <>
                <Button size="lg" variant="primary" className="flex-1 sm:flex-none" loading={busy} onClick={confirmCheck}>
                  {ACTION_LABELS[action].confirm} {seatLabel(view, pending)}
                </Button>
                <Button size="lg" variant="ghost" onClick={() => setPending(null)}>
                  Отмена
                </Button>
              </>
            )}
          </div>
          {me?.alive && (
            <button onClick={() => setLeaving(true)} className="rounded-lg p-2 text-zinc-600 hover:bg-white/5 hover:text-blood-400" title="Выйти из игры" aria-label="Выйти из игры">
              <LogOut className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      <Modal open={leaving} onClose={() => setLeaving(false)} title="Выйти из игры?">
        <p className="mb-5 text-sm text-zinc-400">Вы выбудете из игры и не сможете вернуться в эту партию. Поражение засчитается в статистику.</p>
        <div className="flex gap-2">
          <Button
            variant="primary"
            className="flex-1"
            onClick={async () => {
              setLeaving(false);
              await run('game:leave');
            }}
          >
            Выйти
          </Button>
          <Button variant="secondary" className="flex-1" onClick={() => setLeaving(false)}>
            Остаться
          </Button>
        </div>
      </Modal>
    </div>
  );
}

interface Choices {
  title: string;
  options: { value: number; label: string; selected: boolean }[];
}

/** Явные кнопки для выставления и голосования — продублированы карточками игроков. */
function choiceOptions(view: PlayerView, mode: Mode): Choices | null {
  const me = view.me;
  if (!me) return null;
  const label = (seat: number) => seatLabel(view, seat);
  if (mode === 'nominate') {
    const options = view.players
      .filter((p) => p.alive && p.seat !== me.seat && (!view.nominations.includes(p.seat) || p.seat === me.myNomination))
      .map((p) => ({ value: p.seat, label: label(p.seat), selected: p.seat === me.myNomination }));
    return { title: me.myNomination ? 'Вы выставили — можно поменять:' : 'Выставить на голосование:', options };
  }
  if (mode === 'vote' && view.voting) {
    const v = view.voting;
    const options = v.candidates.map((c) => ({ value: c, label: `Против ${label(c)}`, selected: me.myVote === c }));
    if (v.canSkip) options.push({ value: SKIP_VOTE, label: 'Пропустить голосование', selected: me.myVote === SKIP_VOTE });
    return { title: me.myVote === null ? 'Ваш голос:' : 'Ваш голос (можно поменять):', options };
  }
  return null;
}

function ChoicePanel({ title, options, busy, onChoose }: Choices & { busy: boolean; onChoose(value: number): void }) {
  return (
    <div className="mb-3 rounded-xl border border-gold-400/25 bg-gold-400/5 p-3">
      <p className="mb-2 flex items-center gap-2 text-sm font-medium text-gold-200">
        <MessagesSquare className="h-4 w-4" /> {title}
      </p>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.value}
            disabled={busy || o.selected}
            onClick={() => onChoose(o.value)}
            aria-pressed={o.selected}
            className={clsx(
              'rounded-lg px-3 py-2 text-sm transition',
              o.selected ? 'bg-gold-400 text-ink-950' : 'bg-ink-850 text-zinc-200 hover:bg-white/10 disabled:opacity-60',
            )}
          >
            {o.selected && '✓ '}
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Banner({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <div className="mb-3 animate-fade-in rounded-xl border border-white/5 bg-ink-900/70 px-4 py-3 text-sm leading-relaxed text-zinc-300">{children}</div>;
}

function bannerText(view: PlayerView, mode: Mode, action: NightActionKind | null): ReactNode {
  const me = view.me;
  const who = (seat: number | null) => (seat === null ? '' : seatLabel(view, seat));
  const dead = me && !me.alive;

  switch (view.phase) {
    case 'ROLE_REVEAL':
      return 'Город знакомится с ролями. Запомните свою и нажмите «Я запомнил роль».';
    case 'NIGHT':
      if (mode === 'night' && action === 'MAFIA_KILL') {
        return (
          <>
            {ACTION_LABELS.MAFIA_KILL.hint} При равенстве голосов решает Дон.{' '}
            <b className="text-blood-400">
              {view.mafiaDecision === NO_KILL
                ? 'Решение мафии сейчас: не убивать.'
                : view.mafiaDecision
                  ? `Решение мафии сейчас: ${who(view.mafiaDecision)}`
                  : 'Решения пока нет — промах.'}
            </b>
          </>
        );
      }
      if (mode === 'night' && action) return ACTION_LABELS[action].hint;
      if (dead) return 'Город спит. Вы выбыли — просто наблюдайте и не подсказывайте живым.';
      if (me && me.role !== 'CIVILIAN') return 'Ваш ход сделан. Ждём окончания ночи… Можно отметить подозреваемого для маскировки.';
      return 'Город спит. Чтобы за столом нельзя было понять, у кого активная роль, отметьте, кого подозреваете, — это ни на что не влияет.';
    case 'MORNING':
      return (
        <>
          {view.lastNightDeaths.length === 0 ? (
            <>Город просыпается. <b className="text-emerald-300">Этой ночью никто не погиб.</b></>
          ) : (
            <>
              Город просыпается. Этой ночью {view.lastNightDeaths.length > 1 ? 'погибли' : 'погиб'}:{' '}
              <b className="text-blood-400">{view.lastNightDeaths.map(who).join(', ')}</b>.
            </>
          )}
          {view.lastNightLoverVisit !== null && (
            <>
              {' '}
              Любовница провела ночь с <b className="text-pink-300">{who(view.lastNightLoverVisit)}</b>
              {view.lastNightWithLover !== null
                ? ' — к ней пришла мафия, и гость погиб вместе с ней.'
                : view.silencedSeat !== null
                  ? ' — сегодня он молчит и не голосует.'
                  : '.'}
            </>
          )}
        </>
      );
    case 'DAY_SPEECH': {
      const speaking = view.speakerSeat === me?.seat;
      if (me?.silenced) return 'Вы провели ночь с любовницей: сегодня вы молчите и не голосуете.';
      const noms = view.nominations.length ? ` На голосование выставлены: ${view.nominations.map(who).join(', ')}.` : '';
      if (speaking) {
        return mode === 'nominate'
          ? `Ваша речь. Можете выставить одного игрока на голосование — кнопки ниже или карточка игрока. До конца речи выбор можно поменять.${noms}`
          : `Ваша речь.${noms}`;
      }
      return (
        <>
          Говорит <b className="text-gold-300">{who(view.speakerSeat)}</b>. Очередь: {view.speechOrder.map((s) => `№${s}`).join(' → ')}.{noms}
        </>
      );
    }
    case 'DISCUSSION':
      return (
        <>
          Общее обсуждение — стол решает, кого исключить. На голосовании:{' '}
          <b className="text-gold-300">{view.nominations.map(who).join(', ')}</b>.
        </>
      );
    case 'TIE_SPEECH':
      return (
        <>
          Голоса разделились поровну. Оправдывается <b className="text-gold-300">{who(view.speakerSeat)}</b>, затем повторное голосование.
        </>
      );
    case 'VOTING': {
      const v = view.voting!;
      const prefix = v.round === 2 ? 'Повторное голосование. ' : '';
      if (dead) return `${prefix}Живые голосуют. Вы наблюдаете.`;
      if (me?.silenced) return `${prefix}Сегодня вы не голосуете — ночь с любовницей.`;
      const change = ' Голос можно поменять, пока голосование идёт.';
      if (me?.myVote === SKIP_VOTE) return `${prefix}Ваш голос: пропустить голосование. Проголосовали ${v.votedCount} из ${v.voterCount}.${change}`;
      if (me?.myVote) return `${prefix}Ваш голос: против ${who(me.myVote)}. Проголосовали ${v.votedCount} из ${v.voterCount}.${change}`;
      const skip = v.canSkip ? ' Можно проголосовать за пропуск — это доступно столу один раз за игру.' : '';
      return `${prefix}Выберите, против кого голосуете.${skip}`;
    }
    case 'LAST_WORD': {
      const speaker = who(view.speakerSeat);
      if (view.speakerSeat === me?.seat) return 'Ваше последнее слово. Скажите всё, что думаете, — и завершите речь.';
      return (
        <>
          Последнее слово: <b className="text-gold-300">{speaker}</b>
        </>
      );
    }
    default:
      return null;
  }
}

function SidePanels({ view, secretsVisible }: { view: PlayerView; secretsVisible: boolean }) {
  const me = view.me;
  const panels: ReactNode[] = [];

  if (view.phase === 'VOTING' && view.voting) {
    const v = view.voting;
    panels.push(
      <Card key="vote" className="p-4">
        <h3 className="mb-2 text-sm font-medium text-zinc-300">
          Голосование · проголосовали {v.votedCount} из {v.voterCount}
        </h3>
        <ul className="space-y-1.5 text-sm">
          {[...v.candidates, ...(v.canSkip ? [SKIP_VOTE] : [])].map((c) => {
            const voters = v.votes ? Object.entries(v.votes).filter(([, t]) => t === c).map(([s]) => Number(s)) : null;
            return (
              <li key={c} className="flex items-center justify-between gap-3">
                <span>{c === SKIP_VOTE ? 'Пропустить голосование' : seatLabel(view, c)}</span>
                <span className="text-zinc-500">
                  {voters ? `${voters.length} ${plural(voters.length, 'голос', 'голоса', 'голосов')}${voters.length ? ` (${voters.map((s) => `№${s}`).join(', ')})` : ''}` : 'голоса скрыты'}
                </span>
              </li>
            );
          })}
        </ul>
      </Card>,
    );
  }

  if (view.lastVote && (view.phase === 'LAST_WORD' || view.phase === 'TIE_SPEECH') && Object.keys(view.lastVote.tally).length) {
    panels.push(
      <Card key="last" className="p-4 text-sm">
        <h3 className="mb-2 font-medium text-zinc-300">Итог голосования</h3>
        {Object.entries(view.lastVote.tally).map(([seat, n]) => (
          <p key={seat} className={Number(seat) === view.lastVote!.eliminated ? 'text-blood-400' : 'text-zinc-400'}>
            {seatLabel(view, Number(seat))}: {n} {plural(n, 'голос', 'голоса', 'голосов')}
          </p>
        ))}
      </Card>,
    );
  }

  if (secretsVisible && me && me.checks.length > 0) {
    panels.push(
      <Card key="checks" className="p-4 text-sm">
        <h3 className="mb-2 font-medium text-zinc-300">Мои проверки</h3>
        {me.checks.map((c) => (
          <p key={`${c.day}-${c.target}`} className={c.positive ? 'text-blood-400' : 'text-emerald-300'}>
            Ночь {c.day}: {seatLabel(view, c.target)} —{' '}
            {me.role === 'SHERIFF' ? (c.positive ? 'мафия' : 'не мафия') : c.positive ? 'шериф' : 'не шериф'}
          </p>
        ))}
      </Card>,
    );
  }

  if (secretsVisible && me && isMafiaTeam(me.role)) {
    const team = view.players.filter((p) => p.role && isMafiaTeam(p.role));
    panels.push(
      <Card key="team" className="p-4 text-sm">
        <h3 className="mb-2 font-medium text-zinc-300">Семья</h3>
        {team.map((p) => (
          <p key={p.seat} className={p.alive ? 'text-blood-400' : 'text-zinc-600 line-through'}>
            {seatLabel(view, p.seat)} — {ROLE_INFO[p.role!].name}
          </p>
        ))}
      </Card>,
    );
  }

  if (!panels.length) return null;
  return <div className="mt-4 grid gap-3 sm:grid-cols-2">{panels}</div>;
}
